import React, { useState } from "react";
import { Link } from "react-router-dom";

function About() {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (faqNumber) => {
    setOpenFaq(openFaq === faqNumber ? null : faqNumber);
  };

  return (
    <div className="about-page">
      <div className="mb-4">
        <Link to="/" className="btn btn-outline-primary btn-sm">
          ← Back to Rosters
        </Link>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <h1 className="h3 text-primary mb-4">About Sydney Roster Scheduler</h1>
          
          <p className="lead">
            Sydney Roster Scheduler is Sydney's premier roster tracking platform, providing real-time 
            work schedule information for multiple venues across the city. We aggregate roster data 
            from official venue sources and present it in an easy-to-use, searchable format.
          </p>

          <hr className="my-4" />

          <h2 className="h4 text-primary mb-3">Our Mission</h2>
          <p>
            We created Sydney Roster Scheduler to solve a simple problem: checking work rosters 
            shouldn't be complicated. Instead of visiting multiple venue websites or checking different 
            sources, our platform brings all Sydney roster information together in one convenient location.
          </p>
          <p>
            Whether you're checking the <strong>No 5 Marrickville roster</strong>, looking up shifts at 
            <strong> Ginza Cleveland St</strong>, browsing the <strong>479 Ginza Elizabeth St schedule</strong>, 
            or viewing <strong>The Nightshade Gladesville roster</strong>, we've got you covered.
          </p>

          <hr className="my-4" />

          <h2 className="h4 text-primary mb-3">Venues We Track</h2>
          
          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="card h-100 border-primary">
                <div className="card-header bg-primary text-white">
                  <h3 className="h6 mb-0">🏢 No 5 Marrickville</h3>
                </div>
                <div className="card-body">
                  <p className="mb-2">
                    <strong>Location:</strong> Marrickville, Inner West Sydney
                  </p>
                  <p className="mb-2">
                    <strong>Roster Updates:</strong> Daily (Today & Tomorrow)
                  </p>
                  <p className="mb-0">
                    Complete daily roster schedule for the Marrickville location. View current day and 
                    next day rosters with real-time updates. Check shift times, worker names, and codes.
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="card h-100 border-primary">
                <div className="card-header bg-primary text-white">
                  <h3 className="h6 mb-0">🏢 Ginza Cleveland St</h3>
                </div>
                <div className="card-body">
                  <p className="mb-2">
                    <strong>Location:</strong> Cleveland Street, Sydney
                  </p>
                  <p className="mb-2">
                    <strong>Roster Updates:</strong> Multiple rosters daily
                  </p>
                  <p className="mb-0">
                    Cleveland Street roster information updated daily. Access current shifts, view upcoming 
                    schedules, and search for specific workers or shift times at the Ginza Cleveland location.
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="card h-100 border-primary">
                <div className="card-header bg-primary text-white">
                  <h3 className="h6 mb-0">🏢 479 Ginza Elizabeth St</h3>
                </div>
                <div className="card-body">
                  <p className="mb-2">
                    <strong>Location:</strong> Elizabeth Street, Sydney CBD
                  </p>
                  <p className="mb-2">
                    <strong>Roster Updates:</strong> Multiple rosters daily
                  </p>
                  <p className="mb-0">
                    Elizabeth Street location roster tracking with instant access to work schedules and shift 
                    times. Browse today's roster and check upcoming shifts at 479 Ginza.
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-6 mb-4">
              <div className="card h-100 border-primary">
                <div className="card-header bg-primary text-white">
                  <h3 className="h6 mb-0">🏢 The Nightshade Gladesville</h3>
                </div>
                <div className="card-body">
                  <p className="mb-2">
                    <strong>Location:</strong> Gladesville, Sydney
                  </p>
                  <p className="mb-2">
                    <strong>Roster Updates:</strong> Daily (Today & Tomorrow)
                  </p>
                  <p className="mb-0">
                    Gladesville venue roster updates with current and next-day information. Stay informed 
                    about The Nightshade rosters with our automated tracking system.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <h2 className="h4 text-primary mb-3">Platform Features</h2>
          
          <div className="row">
            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">🔄</div>
                <h3 className="h6 fw-bold">Automatic Updates</h3>
                <p className="text-muted small">
                  Rosters are automatically refreshed daily, ensuring you always have the most current information.
                </p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">📅</div>
                <h3 className="h6 fw-bold">Today & Tomorrow</h3>
                <p className="text-muted small">
                  View both current day and next day rosters for better planning and schedule awareness.
                </p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">🔍</div>
                <h3 className="h6 fw-bold">Search & Filter</h3>
                <p className="text-muted small">
                  Quickly find specific names, codes, or shift times with our powerful search functionality.
                </p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">📊</div>
                <h3 className="h6 fw-bold">Sortable Tables</h3>
                <p className="text-muted small">
                  Organize roster data by name, code, start time, or finish time with one click.
                </p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">📱</div>
                <h3 className="h6 fw-bold">Mobile Optimized</h3>
                <p className="text-muted small">
                  Access rosters on any device - desktop, tablet, or smartphone with responsive design.
                </p>
              </div>
            </div>

            <div className="col-md-4 mb-3">
              <div className="text-center">
                <div className="display-4 mb-2">🆓</div>
                <h3 className="h6 fw-bold">Completely Free</h3>
                <p className="text-muted small">
                  No registration, no login, no payments. Instant access to all roster information.
                </p>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <h2 className="h4 text-primary mb-3">How It Works</h2>
          
          <ol className="list-group list-group-numbered mb-4">
            <li className="list-group-item">
              <strong>Automated Data Collection:</strong> Our system automatically checks each venue's 
              official roster source multiple times daily.
            </li>
            <li className="list-group-item">
              <strong>Data Processing:</strong> Roster information is extracted, cleaned, and formatted 
              for easy reading.
            </li>
            <li className="list-group-item">
              <strong>Real-Time Updates:</strong> New roster data is immediately made available on our 
              platform as soon as it's published by the venues.
            </li>
            <li className="list-group-item">
              <strong>User Access:</strong> You visit our site and instantly see the latest rosters for 
              all tracked venues in one place.
            </li>
          </ol>

          <hr className="my-4" />

          <h2 className="h4 text-primary mb-3">Why Use Sydney Roster Scheduler?</h2>
          
          <div className="alert alert-primary">
            <h3 className="h6 fw-bold mb-2">✨ All-in-One Platform</h3>
            <p className="mb-0">
              Instead of bookmarking and checking multiple venue websites, access all Sydney roster 
              information from a single, convenient location. Save time and stay organized.
            </p>
          </div>

          <div className="alert alert-success">
            <h3 className="h6 fw-bold mb-2">⚡ Always Up-to-Date</h3>
            <p className="mb-0">
              Never miss a roster update. Our automated system ensures you're always seeing the most 
              current schedule information available from each venue.
            </p>
          </div>

          <div className="alert alert-info">
            <h3 className="h6 fw-bold mb-2">🎯 Easy to Use</h3>
            <p className="mb-0">
              Clean, intuitive interface designed for quick roster checks. Find what you need in seconds 
              with search, filter, and sort capabilities.
            </p>
          </div>

          <hr className="my-4" />

          <h2 className="h4 text-primary mb-3">Frequently Asked Questions</h2>
          
          <div className="accordion mb-4" id="faqAccordion">
            <div className="accordion-item">
              <h3 className="accordion-header">
                <button 
                  className={`accordion-button ${openFaq !== 1 ? 'collapsed' : ''}`}
                  type="button" 
                  onClick={() => toggleFaq(1)}
                >
                  How often are rosters updated?
                </button>
              </h3>
              <div className={`accordion-collapse collapse ${openFaq === 1 ? 'show' : ''}`}>
                <div className="accordion-body">
                  Rosters are automatically updated multiple times throughout the day. We check venue 
                  sources regularly to ensure you always have access to the latest information.
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <h3 className="accordion-header">
                <button 
                  className={`accordion-button ${openFaq !== 2 ? 'collapsed' : ''}`}
                  type="button" 
                  onClick={() => toggleFaq(2)}
                >
                  Do I need to create an account?
                </button>
              </h3>
              <div className={`accordion-collapse collapse ${openFaq === 2 ? 'show' : ''}`}>
                <div className="accordion-body">
                  No! Sydney Roster Scheduler is completely free and requires no registration. Simply 
                  visit the site and access all roster information instantly.
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <h3 className="accordion-header">
                <button 
                  className={`accordion-button ${openFaq !== 3 ? 'collapsed' : ''}`}
                  type="button" 
                  onClick={() => toggleFaq(3)}
                >
                  Is the roster information accurate?
                </button>
              </h3>
              <div className={`accordion-collapse collapse ${openFaq === 3 ? 'show' : ''}`}>
                <div className="accordion-body">
                  We pull roster data directly from official venue sources. However, we always recommend 
                  confirming important details with the venue directly, as schedules can change.
                </div>
              </div>
            </div>

            <div className="accordion-item">
              <h3 className="accordion-header">
                <button 
                  className={`accordion-button ${openFaq !== 4 ? 'collapsed' : ''}`}
                  type="button" 
                  onClick={() => toggleFaq(4)}
                >
                  Can you add more venues?
                </button>
              </h3>
              <div className={`accordion-collapse collapse ${openFaq === 4 ? 'show' : ''}`}>
                <div className="accordion-body">
                  We're always looking to expand! If you'd like to suggest a venue for roster tracking, 
                  please contact us through our support channels.
                </div>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          <div className="text-center">
            <h2 className="h4 text-primary mb-3">Ready to Check Rosters?</h2>
            <p className="mb-4">
              Access Sydney roster schedules for No 5 Marrickville, Ginza Cleveland St, 479 Ginza Elizabeth St, 
              and The Nightshade Gladesville - all in one convenient location.
            </p>
            <Link to="/" className="btn btn-primary btn-lg">
              View Rosters Now →
            </Link>
          </div>

          <hr className="my-4" />

          <div className="text-muted small">
            <p className="mb-2">
              <strong>Related Searches:</strong> Sydney roster, Marrickville roster schedule, Cleveland Street 
              venue roster, Elizabeth Street roster tracking, Gladesville schedule, aus99 roster, aus99 forum, 
              Sydney nightlife rosters, venue work schedules Sydney, Marrickville work schedule, Sydney roster 
              aggregator, real-time roster updates Sydney
            </p>
            <p className="mb-0">
              <strong>Service Area:</strong> Sydney, NSW - Marrickville, Cleveland Street, Elizabeth Street, 
              Gladesville, Inner West Sydney, Sydney CBD
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default About;
